import React from "react";
import { Mail, Phone, MapPin, Linkedin, Globe, ArrowUpRight } from "lucide-react";
import { PROFILE, IMAGES } from "../data/content";
import { Eyebrow, Reveal, AvailabilityBadge } from "./shared";
import { OrbitRing, DustMotes, PlanetDot, AstrolabeGlyph } from "./motifs";

export default function Contact() {
  const links = [
    { icon: Mail, label: "Email", value: PROFILE.email, href: `mailto:${PROFILE.email}`, key: "email" },
    { icon: Phone, label: "Phone", value: PROFILE.phone, href: `tel:${PROFILE.phone.replace(/\s/g, "")}`, key: "phone" },
    { icon: Linkedin, label: "LinkedIn", value: "Connect with me", href: PROFILE.linkedin, key: "linkedin" },
    { icon: Globe, label: "Website", value: PROFILE.site, href: `https://${PROFILE.site}`, key: "site" },
  ];
  return (
    <section id="contact" className="relative">
      <div className="relative min-h-[560px] sm:min-h-[600px]">
        <img
          src={IMAGES.contact}
          alt="Warm biophilic atrium at golden hour with brass armillary sphere and cascading pothos"
          className="absolute inset-0 w-full h-full object-cover"
          loading="lazy"
        />
        <div
          className="absolute inset-0"
          style={{ background: "linear-gradient(90deg, rgba(35,31,28,0.92), rgba(92,61,40,0.7) 55%, rgba(92,61,40,0.25))" }}
        />
        {/* orbit ring overlay */}
        <div className="pointer-events-none absolute -right-40 -bottom-32 opacity-40" aria-hidden>
          <div className="orbit-slow">
            <OrbitRing size={640} strokeOpacity={0.5} ticks />
          </div>
        </div>
        <DustMotes count={12} />

        <div className="relative mx-auto max-w-7xl px-5 sm:px-8 pt-24 pb-20 sm:pt-28 sm:pb-24">
          <Reveal>
            <Eyebrow dark n={9} className="mb-6">Contact</Eyebrow>
          </Reveal>
          <Reveal>
            <div className="mb-6">
              <AvailabilityBadge dark />
            </div>
          </Reveal>
          <Reveal>
            <h2 className="headline-serif text-ivory text-4xl sm:text-6xl leading-[1.05] max-w-3xl">
              Let's build something <span className="italic text-honey">calm, warm, and remarkably well run.</span>
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mt-6 text-ivory/85 max-w-xl text-lg flex items-center gap-2">
              <AstrolabeGlyph size={14} color="#E0B84C" />
              <span className="font-mono-tech text-[11px] tracking-[0.28em] uppercase text-honey/80">
                34°01′ N · 118°24′ W · {PROFILE.location}
              </span>
            </p>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="mt-9 grid sm:grid-cols-2 gap-3 max-w-2xl" data-testid="contact-links">
              {links.map((l) => (
                <a
                  key={l.key}
                  href={l.href}
                  target={l.href.startsWith("http") ? "_blank" : undefined}
                  rel="noopener noreferrer"
                  data-testid={`contact-${l.key}`}
                  className="group flex items-center gap-4 rounded-2xl bg-ivory/95 hover:bg-ivory px-5 py-4 transition-colors border border-brass/20 hover:border-brass"
                >
                  <span className="w-10 h-10 grid place-items-center rounded-full bg-terracotta/12 text-terracotta">
                    <l.icon size={18} />
                  </span>
                  <span className="min-w-0">
                    <span className="block font-mono-tech text-[10px] tracking-widest uppercase text-warmmuted flex items-center gap-1.5">
                      <PlanetDot /> {l.label}
                    </span>
                    <span className="block text-caramel font-semibold truncate">{l.value}</span>
                  </span>
                  <ArrowUpRight size={18} className="ml-auto text-warmmuted group-hover:text-terracotta group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-all" />
                </a>
              ))}
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
